import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
    Card,
    Table,
    Icon,
    InfoText,
    Status,
    FilterSetController,
    Filter,
    EmptyState,
    Paginator,
    ToastContext,
    Toggle,
    TableCellDropdown,
    Dropdown,
    Tooltip
} from '@weezevent/nacre';
import LoadingCard from '../../../../components/loadingCard';
import CreateProductModal from './createProductModal';
import Style from './productsTable.module.css';
import moment from 'moment-timezone';
import VariantModel from '../../../../models/variantModel';
import ProductModel from '../../../../models/productModel';

function ProductStatus({ productIsArchived, productIsDisabled }) {
    const { t } = useTranslation();

    const statusMapping = {
        enabled: { color: 'green', text: t('ticket.cross-selling.visible') },
        disabled: { color: 'orange', text: t('ticket.cross-selling.invisible') },
        archived: { color: 'red', text: t('ticket.common.archived') }
    };
    const status = React.useMemo(() => {
        let value = productIsDisabled ? 'disabled' : 'enabled';
        if (productIsArchived) {
            value = 'archived';
        }
        return statusMapping[value];
    }, [productIsArchived, productIsDisabled]);

    return <Status text={status.text} color={status.color} />;
}

function VariantRow({ variant, productId, organization, variantsStatus, setVariantsStatus }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const variantIsDisabled = React.useMemo(() => variantsStatus[variant.id], [variantsStatus, variant.id]);

    const handleToggleActivation = React.useCallback(() => {
        const newDisabledState = !variantIsDisabled;
        const previousDisabledState = variantIsDisabled;

        setVariantsStatus(prevStatus => ({
            ...prevStatus,
            [variant.id]: newDisabledState
        }));

        const updatedVariant = new VariantModel({
            id: variant.id,
            disabled: newDisabledState,
            //TODO: find a better way on Pyvar side, if quota isn't sent here WeezProductVariantSerializer send None to menhir
            quota: variant?.quota_total
        });

        updatedVariant
            .save({
                organizationId: organization.id,
                productId,
                id: variant.id
            })
            .then(() => {
                toast.success(t('ticket.cross-selling.listVariant.variantEdited'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.cross-selling.listVariant.variantEditError'));
                // If the request fail, then we change disabled value back to it's previous state
                setVariantsStatus(prevStatus => ({
                    ...prevStatus,
                    [variant.id]: previousDisabledState
                }));
            });
    }, [variant, variantIsDisabled, organization.id, productId, setVariantsStatus, t, toast]);

    return (
        <Table.Tr>
            <Table.Td>
                <InfoText className={Style['name-cell-size']}>{variant.name}</InfoText>
            </Table.Td>
            <Table.Td>{/* Variant total transaction */}</Table.Td>
            <Table.Td>{variant.price}</Table.Td>
            <Table.Td>
                <Toggle checked={!variantIsDisabled} onChange={() => handleToggleActivation()} />
            </Table.Td>
            <Table.Td>{/* Status for products only */}</Table.Td>
            <Table.Td>{/* Variants action for later */}</Table.Td>
        </Table.Tr>
    );
}

function ProductRowDropDown({ organization, product, sync }) {
    const history = useHistory();
    const [open, setOpen] = React.useState(false);
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const [productIsDisabled, setProductIsDisabled] = React.useState(product.disabled);

    const getInitialVariantsStatus = () => {
        if (!product || !product.variants) {
            return {};
        }

        return product.variants.reduce((acc, variant) => {
            acc[variant.id] = variant.disabled;
            return acc;
        }, {});
    };
    const [variantsStatus, setVariantsStatus] = React.useState(getInitialVariantsStatus);

    const onArchive = React.useCallback(() => {
        product.archived_at = moment.utc().format('YYYY-MM-DDTHH:mm:ss');
        product
            .save({ organizationId: organization.id })
            .then(() => {
                toast.success(t('ticket.cross-selling.archive.success'));
                sync();
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.cross-selling.archive.error'));
            });
    }, [organization, product, toast, sync]);

    const handleToggleActivation = React.useCallback(() => {
        const newDisabledState = !productIsDisabled;
        const previousDisabledState = productIsDisabled;
        setProductIsDisabled(newDisabledState);

        const updatedProduct = new ProductModel({
            id: product.id,
            disabled: newDisabledState
        });

        updatedProduct
            .save({
                organizationId: organization.id,
                productId: product.id
            })
            .then(() => {
                toast.success(t('ticket.cross-selling.add.productEdited'));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.cross-selling.add.productEditError'));
                // If the request fail, then we change disabled value back to it's previous state
                setProductIsDisabled(previousDisabledState);
            });
    }, [productIsDisabled, organization.id, product, t, toast]);

    // When we patch the variants, the back automatically put the product to disabled if there is no more
    // variants enableb, since we're not reloading we also need to keep the behavior accurate on this case
    const hasVariantDisabled = React.useMemo(() => {
        return !Object.values(variantsStatus).some(isDisabled => isDisabled === false);
    }, [variantsStatus]);

    React.useEffect(() => {
        if (hasVariantDisabled && !productIsDisabled) {
            setProductIsDisabled(true);
        }
    }, [hasVariantDisabled, productIsDisabled]);

    const getCategoryIcon = () => {
        switch (product.category) {
            case 1:
                return 'credit-card';
            case 2:
                return 'shirt';
            case 3:
                return 'fee';
            default:
                return 'shirt';
        }
    };

    return (
        <>
            <Table.Tr className={Style['product-row']} onClick={() => setOpen(prev => !prev)}>
                <Table.Td className={Style['product-name-cell']}>
                    <Icon name={'chevron-right'} className={open ? Style['rotate-icon'] : ''} />
                    <div className={Style['icon-name-background']}>
                        <Icon className={Style['no-margin']} name={getCategoryIcon()} size={'huge'} />
                    </div>
                    <InfoText
                        className={Style['name-cell-size']}
                        subtitle={product.for_buyer ? t('ticket.cross-selling.add.radioForBuyer.for_buyer') : t('ticket.cross-selling.add.radioForBuyer.for_order')}
                    >
                        {product.name}
                    </InfoText>
                </Table.Td>
                <Table.Td className={Style['center-text']}>{product.transaction_total_count}</Table.Td>
                <Table.Td /> {/* Price for variants only */}
                <Table.Td>
                    {/* If no variants is enabled for a product, a patch to enable it will do nothing in back */}
                    <Tooltip margin={false} triggerOnHover={hasVariantDisabled} visible={hasVariantDisabled} title={t('ticket.cross-selling.visibility_tooltip')}>
                        <Toggle checked={!productIsDisabled} onChange={() => handleToggleActivation()} disabled={hasVariantDisabled || product.archived_at} />
                    </Tooltip>
                </Table.Td>
                <Table.Td>
                    <ProductStatus productIsArchived={product.archived_at} productIsDisabled={productIsDisabled} />
                </Table.Td>
                <Table.Td className={Style['actions-block']}>
                    <Icon
                        name={'edit'}
                        onClick={e => {
                            history.push(`/ticket/O${organization.id}/products/${product.id}/settings`);
                        }}
                    />
                    <TableCellDropdown>
                        <Dropdown.Item
                            disabled={product.archived_at}
                            onClick={() => {
                                onArchive();
                            }}
                            item={t('ticket.common.action.archive')}
                        />
                    </TableCellDropdown>
                </Table.Td>
            </Table.Tr>
            {open &&
                product.variants?.map((variant, index) => (
                    <VariantRow
                        key={variant.id}
                        variant={variant}
                        productId={product.id}
                        organization={organization}
                        variantsStatus={variantsStatus}
                        setVariantsStatus={setVariantsStatus}
                    />
                ))}
        </>
    );
}

export default function ProductsTable({ organization, products, loading, sync, filters, setFilters, currentPage, setCurrentPage, pageSize, totalCount }) {
    const { t } = useTranslation();
    const [openCreateModal, setOpenCreateModal] = React.useState(false);

    const optionsVisibility = [
        {
            key: 'disabled',
            text: t('ticket.cross-selling.visible'),
            name: 'False'
        },
        {
            key: 'disabled',
            text: t('ticket.cross-selling.notVisible'),
            name: 'True'
        },
        {
            key: 'disabled',
            text: t('ticket.common.archived'),
            name: 'Archived'
        }
    ];

    const optionsTarget = [
        {
            key: 'categoryAttendee',
            text: t('ticket.cross-selling.attendee'),
            name: 'False'
        },
        {
            key: 'categoryBuyer',
            text: t('ticket.cross-selling.buyer'),
            name: 'True'
        }
    ];

    const onChangeSearch = React.useCallback(
        value => {
            let tmpFilters = { ...filters };
            if (value !== '' && value !== null) {
                tmpFilters = { ...tmpFilters, search: value };
            } else {
                delete tmpFilters.search;
            }
            setFilters(tmpFilters);
        },
        [filters]
    );

    return (
        <>
            {/* create modal */}
            <CreateProductModal organization={organization} openModal={openCreateModal} setOpenModal={setOpenCreateModal} sync={sync} />

            <Card>
                <Card.Content>
                    <FilterSetController
                        total={products?.length}
                        isLoadingTotal={loading}
                        labelTotal={t('ticket.cross-selling.labelTotal', { count: products?.length })}
                        labelReset={t('ticket.common.action.reset')}
                        filterLabel={t('ticket.cross-selling.lebelFilters')}
                        initialValues={filters}
                        onChange={values => {
                            setFilters(values);
                        }}
                        search={{
                            onChange: (_, value) => {
                                onChangeSearch(value);
                            },
                            handleReset: (_, value) => {
                                onChangeSearch(null);
                            },
                            placeholder: t('ticket.common.action.search'),
                            value: filters?.search || ''
                        }}
                        onReset={() => {}}
                    >
                        <Filter name="disabled" labelName={t('ticket.common.status')} options={optionsVisibility} index="1" />
                        <Filter name="for_buyer" labelName={t('ticket.cross-selling.target')} options={optionsTarget} index="2" />
                    </FilterSetController>
                    <LoadingCard loading={loading}>
                        {products?.length > 0 ? (
                            <Table>
                                <Table.THead>
                                    <th>{t('ticket.cross-selling.listProducts.columns.cross-selling')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.sales')}</th>
                                    {/* <th>{t('Quota')}</th> */}
                                    <th>{t('ticket.cross-selling.listProducts.columns.price')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.activation')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.status')}</th>
                                    <th>{t('ticket.cross-selling.listProducts.columns.actions')}</th>
                                </Table.THead>
                                <Table.TBody>
                                    {products?.map((product, index) => (
                                        <ProductRowDropDown key={product.id} organization={organization} product={product} sync={sync} />
                                    ))}
                                </Table.TBody>
                            </Table>
                        ) : (
                            <Card>
                                <Card.Content>
                                    <EmptyState
                                        icon={<Icon name="list" />}
                                        title={t('ticket.cross-selling.addObject')}
                                        subtitle={t('ticket.cross-selling.empty.sub')}
                                        labelButton={t('ticket.cross-selling.empty.labelButton')}
                                        onClick={() => setOpenCreateModal(true)}
                                    />
                                </Card.Content>
                            </Card>
                        )}
                    </LoadingCard>
                </Card.Content>
                <div className={Style['paginator-align']}>
                    <Paginator
                        currentPage={currentPage}
                        onPageChange={numberPage => {
                            setCurrentPage(numberPage);
                        }}
                        totalCount={totalCount !== '-' ? +totalCount : 0}
                        pageSize={pageSize}
                    />
                </div>
            </Card>
        </>
    );
}
